import React, { useState, useEffect } from 'react';
import LOGO from '../../assets/img/logo.jpg';
import LOGO_SHORT from '../../assets/img/logo_short.png';
import {
  FaSignOutAlt,
  FaCalendarAlt,
  FaUsers,
  FaCar,
  FaExclamationTriangle,
  FaCreditCard,
  FaList,
  FaCogs,
  FaUserTie,
  FaMoneyBillWave,
  FaClipboardList,
  FaChevronDown,
  FaChevronRight,
  FaTimes,
} from 'react-icons/fa';
import { logout } from '../../utils/auth';
import { getLandingPath, isClient } from '../../utils/access';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import VersionSwitch from '../../components/shared/VersionSwitch';

export default function SidebarV2({
  collapsed = false,
  mobileOpen = false,
  onCloseMobile,
  onToggleCollapse,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const menu = useSelector((state) => state.user.menu || {});
  const name = useSelector((state) => state.user.name);
  const userType = useSelector((state) => state.user.userType);
  const role = useSelector((state) => state.user.role);
  const client = isClient(userType);
  const landingPath = getLandingPath(userType);
  const cname = name || (client ? 'Cliente' : 'Usuario');
  const roleLabel = role || (client ? 'Cliente' : 'Operador');

  const [openCategories, setOpenCategories] = useState({});
  const categories = Object.keys(menu).filter((cat) =>
    (menu[cat] || []).some((item) => !client || item.url !== '/home')
  );

  // Auto-expand current active category based on URL
  useEffect(() => {
    const cats = Object.keys(menu);
    const activeCatMap = {};
    const currentUrl = location.pathname.startsWith('/modifyInspection/')
      ? '/createinspection/'
      : location.pathname + '/';

    cats.forEach((cat) => {
      const sublist = menu[cat] || [];
      const hasActive = sublist.some((item) => currentUrl.startsWith(item.url + '/'));
      if (hasActive) {
        activeCatMap[cat] = true;
      }
    });

    setOpenCategories((prev) => ({ ...prev, ...activeCatMap }));
  }, [location.pathname, menu]);

  const toggleCategory = (cat) => {
    setOpenCategories((prev) => ({
      ...prev,
      [cat]: !prev[cat],
    }));
  };

  const handleNavigate = (url) => {
    navigate(url);
    if (onCloseMobile) {
      onCloseMobile();
    }
  };

  const getIcon = (cat) => {
    switch (cat) {
      case 'Agendas': return <FaCalendarAlt />;
      case 'Usuarios': return <FaUsers />;
      case 'Peritaje': return <FaCar />;
      case 'Placas Criticas': return <FaExclamationTriangle />;
      case 'Pagos': return <FaCreditCard />;
      case 'Ordenes': return <FaList />;
      case 'Caja Menor': return <FaMoneyBillWave />;
      case 'Configuracion': return <FaCogs />;
      case 'PQR': return <FaClipboardList />;
      case 'Clientes': return <FaUserTie />;
      default: return <FaCar />;
    }
  };

  const isRouteActive = (url) => {
    return location.pathname === url || location.pathname.startsWith(`${url}/`);
  };

  return (
    <aside className={`v2-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'open-mobile' : ''}`}>
      <div className="v2-brand">
        <div
          className={`d-flex align-items-center gap-2 ${collapsed && !mobileOpen ? 'justify-content-center' : 'flex-grow-1'}`}
          onClick={() => handleNavigate(landingPath)}
          style={{ cursor: 'pointer' }}
        >
          <img
            src={collapsed && !mobileOpen ? LOGO_SHORT : LOGO}
            alt="Integra Motor"
            style={{
              maxHeight: 38,
              maxWidth: collapsed && !mobileOpen ? 38 : '100%',
              objectFit: 'contain',
              transition: 'all 200ms ease',
            }}
          />
        </div>

        {/* Mobile Close Button */}
        {mobileOpen && (
          <button
            type="button"
            className="v2-close-drawer-btn d-md-none"
            onClick={onCloseMobile}
            aria-label="Cerrar menú"
          >
            <FaTimes />
          </button>
        )}
      </div>

      <div className="v2-sidebar-content">
        <div className="v2-section-title">Navegación</div>

        {!client && (
          <div
            className={`v2-nav-item ${isRouteActive('/home') ? 'active' : ''}`}
            onClick={() => handleNavigate('/home')}
            title="Inicio / Dashboard"
          >
            <span className="v2-nav-icon"><FaCar /></span>
            <span className="v2-nav-label">Inicio</span>
          </div>
        )}

        {categories.map((cat, idx) => {
          const items = (menu[cat] || []).filter(
            (item) => !client || item.url !== '/home'
          );
          const isOpen = !!openCategories[cat];
          const hasActiveItem = items.some((item) => isRouteActive(item.url));

          return (
            <div key={idx} className="v2-nav-group mb-1">
              <div
                className={`v2-nav-item ${hasActiveItem && !isOpen ? 'active' : ''}`}
                onClick={() => toggleCategory(cat)}
                title={cat}
              >
                <span className="v2-nav-icon">{getIcon(cat)}</span>
                <span className="v2-nav-label flex-grow-1">{cat}</span>
                {(!collapsed || mobileOpen) && (
                  <span className="v2-chevron text-muted" style={{ fontSize: '0.75rem' }}>
                    {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                  </span>
                )}
              </div>

              {isOpen && (!collapsed || mobileOpen) && (
                <div className="ps-3 pt-1">
                  {items.map((sub, sIdx) => {
                    const active = isRouteActive(sub.url);
                    return (
                      <div
                        key={sIdx}
                        className={`v2-nav-item ${active ? 'active' : ''}`}
                        onClick={() => handleNavigate(sub.url)}
                        style={{ fontSize: '0.82rem', padding: '8px 10px' }}
                      >
                        <span className="v2-nav-label">{sub.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="v2-sidebar-footer">
        <VersionSwitch variant="sidebar" />

        <div className="v2-user-profile">
          <div className="v2-avatar">
            {cname ? cname.charAt(0).toUpperCase() : 'U'}
          </div>
          <div className="v2-user-details">
            <div className="v2-user-name" title={cname}>{cname}</div>
            <div className="v2-user-role">{roleLabel}</div>
          </div>
          <button
            type="button"
            className="v2-logout-btn"
            onClick={logout}
            title="Cerrar Sesión"
          >
            <FaSignOutAlt />
          </button>
        </div>
      </div>
    </aside>
  );
}
